import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Globe, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAppStore } from '../store/appStore';
import { detectRegion, getAvailableRegions, getRegionConfig, getTaxName } from '../lib/regionConfig';

/**
 * First-login popup asking the user where they are, so their currency, tax
 * and fiscal year are right from the first job rather than silently
 * Australian. It shows while settings carry region_confirmed === false -
 * only a fresh account's settings do; documents from before the field
 * existed lack it and never trigger the popup. Saving any region (here or
 * on the Settings page) sets region_confirmed on the server, so it asks
 * exactly once. Closing without saving keeps the popup for the next visit.
 */
export function RegionPrompt() {
  const { settings, updateSettings } = useAppStore();
  const [dismissed, setDismissed] = useState(false);
  const [region, setRegion] = useState(null);
  const [saving, setSaving] = useState(false);

  const shouldAsk = settings?.region_confirmed === false;
  if (!shouldAsk || dismissed) return null;

  // Pre-select the region the browser's timezone and language suggest, so
  // most users only have to confirm. Their explicit pick always wins.
  const selected = region || detectRegion() || settings.region || 'AU';
  const config = getRegionConfig(selected);

  const handleSave = async () => {
    setSaving(true);
    try {
      // On success the store's settings carry region_confirmed: true and the
      // dialog unmounts by itself.
      await updateSettings({ region: selected, gst_rate: config.tax.defaultRate });
      toast.success(`Region set to ${config.name}`);
    } catch {
      toast.error('Failed to save your region — you can set it later in Settings');
      setDismissed(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(isOpen) => !isOpen && setDismissed(true)}>
      <DialogContent className="max-w-md bg-parchment rounded-highlight shadow-modal" data-testid="region-prompt">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 text-[28px] font-normal leading-tight">
            <Globe className="w-[18px] h-[18px] text-clay shrink-0" />
            Where are you based?
          </DialogTitle>
          <DialogDescription>
            Your region sets the currency, tax and financial year on every
            estimate and invoice. You can change it any time in Settings.
          </DialogDescription>
        </DialogHeader>
        <div className="py-2 space-y-4">
          <Select value={selected} onValueChange={setRegion}>
            <SelectTrigger data-testid="region-prompt-select">
              <SelectValue placeholder="Select your region" />
            </SelectTrigger>
            <SelectContent>
              {getAvailableRegions().map((r) => (
                <SelectItem key={r.value} value={r.value}>
                  {r.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="text-sm text-muted-foreground">
            <p>Currency: {config.currency.code} ({config.currency.symbol})</p>
            <p>Tax: {getTaxName(selected)} {config.tax.defaultRate}%</p>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={handleSave} disabled={saving} data-testid="region-prompt-save">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            Confirm region
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
